import {
  Box,
  Typography,
  LinearProgress,
  Alert,
  Stack,
} from '@mui/material';

interface CompletenessAnalysis {
  score: number;
  totalRequirements: number;
  addressedRequirements: number;
  partiallyAddressed: number;
  notAddressed: number;
  gaps: Array<{
    requirement: string;
    issue: string;
    severity: 'high' | 'medium' | 'low';
  }>;
}

interface CompletenessDetailsProps {
  data: CompletenessAnalysis;
}

export default function CompletenessDetails({ data }: CompletenessDetailsProps) {
  const getScoreColor = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const getSeverityColor = (severity: 'high' | 'medium' | 'low') => {
    switch (severity) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
    }
  };

  return (
    <Stack spacing={2}>
      {/* Requirements Coverage Progress */}
      <Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Requirements Coverage
        </Typography>
        <LinearProgress
          variant="determinate"
          value={(data.addressedRequirements / data.totalRequirements) * 100}
          color={getScoreColor(data.score) as 'success' | 'warning' | 'error'}
          sx={{ height: 8, borderRadius: 4 }}
        />
        <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
          {data.addressedRequirements} of {data.totalRequirements} requirements fully addressed
          {data.partiallyAddressed > 0 && ` (${data.partiallyAddressed} partially addressed)`}
        </Typography>
      </Box>

      {/* Identified Gaps */}
      {data.gaps.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom fontWeight={600}>
            Identified Gaps:
          </Typography>
          <Stack spacing={1}>
            {data.gaps.map((gap, idx) => (
              <Alert key={idx} severity={getSeverityColor(gap.severity)}>
                <Stack spacing={0.5}>
                  <Typography variant="body2" fontWeight={600}>
                    {gap.requirement}
                  </Typography>
                  <Typography variant="body2">{gap.issue}</Typography>
                </Stack>
              </Alert>
            ))}
          </Stack>
        </Box>
      )}

      {/* Summary Stats */}
      {data.gaps.length === 0 && (
        <Alert severity="success">
          All requirements have been addressed comprehensively.
        </Alert>
      )}
    </Stack>
  );
}
